/**
 * @fileoverview Mock-only in-memory Target store.
 * @description Seeds from targets-seed.json and holds it in a module-level array, same
 * in-memory-only pattern as tenants-mock-store.ts. Every mutation that changes a target's owner or
 * completion also updates the parent tenant through tenants-mock-store.ts, so the two stores never
 * drift apart the way today's separate per-page fixtures do. Never imported outside this feature's
 * mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { resolveScopedUserIds } from '@/features/sales/lib/sales-hierarchy.utils'
import { isTargetOverdue } from '@/features/sales/lib/target-status.utils'
import { addActivity } from '@/features/sales/mocks/activities-mock-store'
import { listSalesRoster } from '@/features/sales/mocks/sales-roster-mock-store'
import {
  findTenantById,
  markTenantActiveClient,
  recordTenantOwnershipChange,
} from '@/features/sales/mocks/tenants-mock-store'

import seedData from './targets-seed.json'

import type { Target } from '@/features/sales/types/tenant.types'

const targets: Target[] = seedData.map((target) => ({
  ...target,
  periodType: target.periodType as Target['periodType'],
  priority: target.priority as Target['priority'],
  stage: target.stage as Target['stage'],
  status: target.status as Target['status'],
}))

/** A Target plus its always-current derived `overdue` flag — see target-status.utils.ts. */
export type TargetWithOverdue = Target & { overdue: boolean }

/**
 * @description Attaches the derived `overdue` flag to a target, computed fresh against the current
 * time rather than stored (see target-status.utils.ts's isTargetOverdue doc comment for why).
 * @param {Target} target - The target to annotate.
 * @returns {TargetWithOverdue} The same target plus its current `overdue` value.
 */
function withOverdue(target: Target): TargetWithOverdue {
  return { ...target, overdue: isTargetOverdue(target) }
}

export interface TargetFilters {
  tenantId?: string
  ownerId?: string
  ownerIds?: string[]
  stage?: Target['stage']
  status?: Target['status']
  periodType?: Target['periodType']
  overdue?: boolean
}

/**
 * @description Lists targets matching every supplied filter (filters left `undefined` are
 * ignored — an empty string is a supplied value, not an absent one, since Super Admin's
 * viewerUserId is legitimately `''` and must correctly match zero targets, not every target),
 * each annotated with its current derived `overdue` flag.
 * @param {TargetFilters} filters - The filters to apply.
 * @returns {TargetWithOverdue[]} Every matching target.
 */
export function listTargets(filters: TargetFilters = {}): TargetWithOverdue[] {
  return targets
    .filter((target) => {
      if (filters.tenantId && target.tenantId !== filters.tenantId) return false
      // `!== undefined`, not plain truthiness — see listTenants's own ownerId check in
      // tenants-mock-store.ts for why: Super Admin's viewerUserId is legitimately `''`, and a
      // falsy-but-supplied ownerId must still match nothing rather than being skipped entirely.
      if (filters.ownerId !== undefined && target.ownerId !== filters.ownerId) return false
      if (filters.ownerIds && !filters.ownerIds.includes(target.ownerId)) return false
      if (filters.stage && target.stage !== filters.stage) return false
      if (filters.status && target.status !== filters.status) return false
      if (filters.periodType && target.periodType !== filters.periodType) return false
      return true
    })
    .map(withOverdue)
    .filter((target) => filters.overdue === undefined || target.overdue === filters.overdue)
}

/**
 * @description Finds one target by id.
 * @param {string} id - The target id to look up.
 * @returns {TargetWithOverdue | undefined} The matching target, or undefined.
 */
export function findTargetById(id: string): TargetWithOverdue | undefined {
  const target = targets.find((candidate) => candidate.id === id)
  return target ? withOverdue(target) : undefined
}

export interface AssignTargetInput {
  tenantId: string
  name: string
  ownerId: string
  periodType: Target['periodType']
  periodLabel: string
  startDate: string
  endDate: string
  priority: Target['priority']
  actingUserId: string
}

let nextSequence = targets.length + 1

/**
 * @description Creates a new target against a tenant and sets the tenant's current owner — "Each
 * bank has one current target owner... owner controls sales-stage updates and receives completion
 * credit", true whether the assignee is a Manager retaining the bank personally or an Executive
 * ("A Sales Manager can personally handle retained banks using the same owner actions as an
 * Executive"). Refuses to create a second open target against a tenant that already has one — see
 * Sales Pipeline's "Repeat Assignment" open decision in tenant.types.ts's Target doc comment.
 * @param {AssignTargetInput} input - The target's details and the acting user recording the
 * assignment.
 * @returns {TargetWithOverdue | null} The created target, or null if the tenant already has an open
 * target or doesn't exist.
 */
export function assignTarget(input: AssignTargetInput): TargetWithOverdue | null {
  const tenant = findTenantById(input.tenantId)
  if (!tenant) return null

  const hasOpenTarget = targets.some(
    (target) => target.tenantId === input.tenantId && target.status === 'Outstanding',
  )
  if (hasOpenTarget) return null

  const target: Target = {
    id: `TGT-${String(3000 + nextSequence)}`,
    tenantId: input.tenantId,
    name: input.name,
    periodType: input.periodType,
    periodLabel: input.periodLabel,
    startDate: input.startDate,
    endDate: input.endDate,
    priority: input.priority,
    stage: 'Prospect',
    stageReason: null,
    status: 'Outstanding',
    ownerId: input.ownerId,
    completedAt: null,
    confirmedByUserId: null,
    nextFollowUpAt: null,
    createdAt: new Date().toISOString(),
  }
  nextSequence += 1
  targets.push(target)

  recordTenantOwnershipChange(input.tenantId, {
    fromOwnerId: tenant.currentOwnerId,
    toOwnerId: input.ownerId,
    actingUserId: input.actingUserId,
    transferredAt: target.createdAt,
  })

  return withOverdue(target)
}

export interface UpdateStageInput {
  stage: Target['stage']
  reason?: string
  actingUserId: string
}

/**
 * @description Updates a target's sales stage. Enforces "Only the current target owner can
 * manually update sales stages" (the acting user must be the target's own ownerId — not any
 * reporting senior) and "Marking Lost or On Hold requires a reason note". Refuses to set 'Active
 * Client' directly: "Active Client cannot be set through an ordinary manual stage update" — that
 * transition only happens via confirmPayment.
 * @param {string} id - The target id to update.
 * @param {UpdateStageInput} input - The new stage, its acting user, and the reason note (required
 * for Lost/On Hold).
 * @returns {TargetWithOverdue | { error: string } | null} The updated target; an error object for a
 * validation failure (wrong actor, missing reason, or an attempted direct Active Client set); or
 * null if no target matched the id.
 */
export function updateTargetStage(
  id: string,
  input: UpdateStageInput,
): TargetWithOverdue | { error: string } | null {
  const target = targets.find((candidate) => candidate.id === id)
  if (!target) return null

  if (target.ownerId !== input.actingUserId) {
    return { error: 'Only the current target owner can update the sales stage.' }
  }
  if (input.stage === 'Active Client') {
    return { error: 'Active Client can only be set by confirming payment.' }
  }
  if ((input.stage === 'Lost' || input.stage === 'On Hold') && !input.reason?.trim()) {
    return { error: 'A reason note is required for Lost or On Hold.' }
  }

  target.stage = input.stage
  target.stageReason =
    input.stage === 'Lost' || input.stage === 'On Hold' ? (input.reason ?? null) : null
  return withOverdue(target)
}

export interface ReassignTargetInput {
  toOwnerId: string
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Transfers a target's ownership. Enforces Sales Pipeline's "Ownership Transfer"
 * rule: a Manager/Head may only reassign within their own permitted scope — both the current owner
 * and the new owner must fall inside the acting user's resolved downline — while Super Admin may
 * transfer across Heads freely. A transfer that would move a bank *out* of the acting user's scope
 * (i.e. to someone outside their downline) is exactly the "across Sales Heads" case the spec
 * restricts to Super Admin, so this one check covers both rules without special-casing "cross-Head"
 * separately. Target dates are never touched — "Target Period and Dates: Remain unchanged" — and
 * the transfer is recorded on the parent tenant's ownerHistory so the former owner's read-only
 * access and the new owner's current assignment are both derivable from one record.
 * @param {string} id - The target id to reassign.
 * @param {ReassignTargetInput} input - The new owner, the acting user, and their role.
 * @returns {TargetWithOverdue | { error: string } | null} The updated target; an error object if
 * the acting user's scope doesn't cover both the current and new owner; or null if no target
 * matched the id.
 */
export function reassignTarget(
  id: string,
  input: ReassignTargetInput,
): TargetWithOverdue | { error: string } | null {
  const target = targets.find((candidate) => candidate.id === id)
  if (!target) return null

  const tenant = findTenantById(target.tenantId)
  if (tenant && tenant.status !== 'Prospect') {
    return { error: 'Sales users have read-only access once a bank becomes an Active Client.' }
  }

  const scopedIds = resolveScopedUserIds(
    input.actingUserId,
    input.actingUserRole,
    listSalesRoster(),
  )
  const withinScope =
    scopedIds === null ||
    (scopedIds.includes(target.ownerId) && scopedIds.includes(input.toOwnerId))
  if (!withinScope) {
    return {
      error:
        'Reassigning across Sales Heads requires Super Admin. This transfer falls outside your permitted hierarchy.',
    }
  }

  recordTenantOwnershipChange(target.tenantId, {
    fromOwnerId: target.ownerId,
    toOwnerId: input.toOwnerId,
    actingUserId: input.actingUserId,
    transferredAt: new Date().toISOString(),
  })
  target.ownerId = input.toOwnerId
  return withOverdue(target)
}

/**
 * @description Confirms initial payment: completes the target against its *original* period
 * (retaining the actual confirmation date separately — "There is no separate Completed Late
 * status") and converts the tenant to Active Client. The only path that can ever set stage
 * 'Active Client' or tenant status 'Active Client'.
 * @param {string} id - The target id whose payment is being confirmed.
 * @param {string} confirmedByUserId - The confirming Super Admin's user id.
 * @returns {TargetWithOverdue | null} The completed target, or null if no target matched the id.
 */
export function confirmTargetPayment(
  id: string,
  confirmedByUserId: string,
): TargetWithOverdue | null {
  const target = targets.find((candidate) => candidate.id === id)
  if (!target) return null

  target.status = 'Completed'
  target.stage = 'Active Client'
  target.stageReason = null
  target.completedAt = new Date().toISOString().slice(0, 10)
  target.confirmedByUserId = confirmedByUserId
  markTenantActiveClient(target.tenantId)
  return withOverdue(target)
}

export interface UpdateTargetDetailsInput {
  name: string
  periodType: Target['periodType']
  periodLabel: string
  startDate: string
  endDate: string
  priority: Target['priority']
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Lists the human-readable "field: old → new" changes an update would make, for the
 * bank's history entry. Kept separate so updateTargetDetails stays within the complexity limit.
 * @param {Target} target - The target before the update.
 * @param {UpdateTargetDetailsInput} input - The proposed new values.
 * @returns {string[]} One line per changed field; empty when nothing changed.
 */
function describeTargetChanges(target: Target, input: UpdateTargetDetailsInput): string[] {
  const changes: string[] = []
  if (input.name.trim() !== target.name) {
    changes.push(`Name: ${target.name} → ${input.name.trim()}`)
  }
  if (input.startDate !== target.startDate || input.endDate !== target.endDate) {
    changes.push(`Period: ${target.periodLabel} → ${input.periodLabel}`)
  }
  if (input.priority !== target.priority) {
    changes.push(`Priority: ${target.priority} → ${input.priority}`)
  }
  return changes
}

/**
 * @description Corrects an open target's name, period (and its derived dates) and priority — "Edit
 * Target". Permitted only to the owner's authorised seniors: a reporting Manager/Head whose
 * downline includes the owner, or Super Admin — never the owner themselves, who keeps only the
 * owner actions (Update Stage, Schedule Follow-up). Only while the target is Outstanding and its
 * bank is still a Prospect, so a Completed target keeps the original period its completion credit
 * was earned against. Ownership is never touched (that's Reassign). Every effective change is
 * appended to the bank's activity history as a Note authored by the acting user, with each field's
 * old and new value; a save that changes nothing records nothing.
 * @param {string} id - The target id to update.
 * @param {UpdateTargetDetailsInput} input - The new name/period/priority and the acting user/role.
 * @returns {TargetWithOverdue | { error: string } | null} The updated target; an error object for
 * a permission or state violation; or null if no target matched the id.
 */
export function updateTargetDetails(
  id: string,
  input: UpdateTargetDetailsInput,
): TargetWithOverdue | { error: string } | null {
  const target = targets.find((candidate) => candidate.id === id)
  if (!target) return null

  const tenant = findTenantById(target.tenantId)
  if (target.status !== 'Outstanding' || (tenant && tenant.status !== 'Prospect')) {
    return { error: 'Only an open target on a prospect bank can be edited.' }
  }

  const scopedIds = resolveScopedUserIds(
    input.actingUserId,
    input.actingUserRole,
    listSalesRoster(),
  )
  const isSenior =
    target.ownerId !== input.actingUserId &&
    (scopedIds === null || scopedIds.includes(target.ownerId))
  if (!isSenior) {
    return {
      error: "Only the owner's reporting Manager/Head or Super Admin can edit this target.",
    }
  }

  if (!input.name.trim()) {
    return { error: 'Give the target a name.' }
  }

  const changes = describeTargetChanges(target, input)
  if (changes.length === 0) {
    return withOverdue(target)
  }

  target.name = input.name.trim()
  target.periodType = input.periodType
  target.periodLabel = input.periodLabel
  target.startDate = input.startDate
  target.endDate = input.endDate
  target.priority = input.priority

  addActivity(target.tenantId, {
    type: 'Note',
    details: `Target updated — ${changes.join('; ')}`,
    authorId: input.actingUserId,
  })

  return withOverdue(target)
}

export interface ScheduleFollowUpInput {
  nextFollowUpAt: string
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Records the next planned follow-up against a target — My Page's "Schedule
 * Follow-up: Record the next planned follow-up against the bank and display it in upcoming
 * activities." Permitted to the same audience as adding an activity: the current target owner, or
 * an authorised reporting Manager/Head within the owner's hierarchy scope (Super Admin unscoped) —
 * see classifyTenantAccess's isOwner/isInScope, which TenantDetailModal already applies to Add
 * Activity. Never touches stage, status, or stageReason: "Recording an activity does not
 * automatically change the bank's sales stage or complete its target" applies equally here.
 * @param {string} id - The target id to schedule a follow-up against.
 * @param {ScheduleFollowUpInput} input - The follow-up's date/time and the acting user/role.
 * @returns {TargetWithOverdue | { error: string } | null} The updated target; an error object if
 * the acting user is outside the owner's hierarchy scope; or null if no target matched the id.
 */
export function scheduleFollowUp(
  id: string,
  input: ScheduleFollowUpInput,
): TargetWithOverdue | { error: string } | null {
  const target = targets.find((candidate) => candidate.id === id)
  if (!target) return null

  const scopedIds = resolveScopedUserIds(
    input.actingUserId,
    input.actingUserRole,
    listSalesRoster(),
  )
  const authorised =
    target.ownerId === input.actingUserId ||
    scopedIds === null ||
    scopedIds.includes(target.ownerId)
  if (!authorised) {
    return {
      error:
        'Only the target owner or an authorised reporting Manager/Head can schedule a follow-up.',
    }
  }

  target.nextFollowUpAt = input.nextFollowUpAt
  return withOverdue(target)
}

/**
 * @description Bulk-transfers every outstanding target owned by `fromOwnerId` to `toOwnerId` — User
 * Management's deactivation flow: "All incomplete targets transfer to the user's reporting
 * manager... Original target periods and dates remain unchanged... Each ownership transfer is
 * recorded in the bank's history." Deliberately separate from reassignTarget: that function also
 * enforces the manual "within your own hierarchy scope" rule for an ordinary Reassign Owner action,
 * which doesn't apply here — a deactivation-triggered transfer to the departing user's own
 * reporting manager is authorised by the deactivation itself, not by the acting admin's individual
 * hierarchy scope. Archived, Lost and On Hold prospects' targets are included here exactly like any
 * other outstanding one, since this only filters on target status, never on sales stage or the
 * tenant's archived flag. Completed targets are never touched, so their completion credit is
 * untouched too.
 * @param {string} fromOwnerId - The departing user whose outstanding targets are being moved.
 * @param {string} toOwnerId - The successor taking over ownership.
 * @param {string} actingUserId - The administrator performing the deactivation, recorded on each
 * transfer.
 * @returns {TargetWithOverdue[]} Every target that was moved.
 */
export function transferOutstandingTargets(
  fromOwnerId: string,
  toOwnerId: string,
  actingUserId: string,
): TargetWithOverdue[] {
  const affected = targets.filter(
    (target) => target.ownerId === fromOwnerId && target.status === 'Outstanding',
  )

  for (const target of affected) {
    recordTenantOwnershipChange(target.tenantId, {
      fromOwnerId: target.ownerId,
      toOwnerId,
      actingUserId,
      transferredAt: new Date().toISOString(),
    })
    target.ownerId = toOwnerId
  }

  return affected.map(withOverdue)
}
